<template>
  <Transition name="slide-fade">
    <div
      v-if="isOffline && !dismissed"
      class="pwa-offline-indicator fixed top-0 left-0 m-4 p-3 bg-amber-50 border border-amber-200 rounded-lg shadow-md flex items-center gap-3 max-w-xs z-50"
      role="alert"
      aria-live="polite"
    >
      <!-- Wifi Icon -->
      <div class="shrink-0 bg-amber-100 p-2 rounded-full">
        <v-icon
          icon="fas fa-wifi"
          class="text-amber-600"
          size="small"
        />
      </div>

      <!-- Content -->
      <div class="grow">
        <p class="text-sm font-medium text-amber-800">Modo sin conexión</p>
        <p class="text-xs text-amber-700">Funcionalidad limitada</p>
      </div>

      <!-- Close Button -->
      <button
        @click="dismissIndicator"
        class="shrink-0 text-amber-500 hover:text-amber-700 focus:outline-none"
        aria-label="Cerrar notificación"
      >
        <v-icon
          icon="fas fa-times"
          size="x-small"
        />
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
  import { useConnectivity } from '@/app/pwa/composables/useConnectivity';
  import { ref, watch } from 'vue';

  /**
   * @author Omar Sánchez Martínez
   * @updated 2025-11-19
   * @description PWA Offline Indicator Component
   *
   * Displays a compact, non-intrusive notification when the user
   * is offline. Uses the useConnectivity composable to track online/offline state.
   *
   * Features:
   * - Compact design that doesn't interfere with main content
   * - Positioned in top-left corner with subtle animation
   * - Only displays when offline
   * - Uses warning color (amber) for visibility
   * - Includes WiFi off icon
   * - Dismissible with automatic reappearance on status change
   * - Automatically hides when connection is restored
   * - Matches Munzen design system with Tailwind classes
   *
   * @example
   * <PwaOfflineIndicator />
   */

  const { isOffline } = useConnectivity();
  const dismissed = ref(false);

  /**
   * Dismiss the offline indicator
   * Will reappear if connection status changes
   */
  const dismissIndicator = () => {
    dismissed.value = true;
  };

  // Reset dismissed state when online/offline status changes
  watch(isOffline, () => {
    dismissed.value = false;
  });
</script>

<style scoped>
  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: all 0.3s ease;
  }

  .slide-fade-enter-from,
  .slide-fade-leave-to {
    transform: translateY(-20px);
    opacity: 0;
  }
</style>
